import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import pettyCashService from '../services/pettyCashService';
import styles from '../assets/pettyCash.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PettyCash = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false); // Changed to false to try API first
  const [pettyCashData, setPettyCashData] = useState([]);
  const [summary, setSummary] = useState({});
  const [currentMonth, setCurrentMonth] = useState('');
  
  // Table controls
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState({
    employee: true,
    date: true,
    description: true,
    amount: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  // State for sorting
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Generate mock data
  const generateMockData = () => {
    const mockEmployees = [
      'John Smith',
      'Sarah Johnson',
      'Michael Brown',
      'Emily Davis',
      'David Wilson',
      'Lisa Anderson',
      'Robert Taylor',
      'Jennifer Martinez'
    ];

    const mockDescriptions = [
      'Office supplies',
      'Travel expenses',
      'Client entertainment',
      'Equipment maintenance',
      'Software licenses',
      'Training materials',
      'Marketing materials',
      'Utilities payment',
      'Cleaning services',
      'Security services'
    ];

    return Array.from({ length: 50 }, (_, index) => ({
      id: index + 1,
      employee: mockEmployees[Math.floor(Math.random() * mockEmployees.length)],
      date: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString('en-GB'),
      description: mockDescriptions[Math.floor(Math.random() * mockDescriptions.length)],
      amount: (Math.random() * 500 + 50).toFixed(2),
      file: Math.random() > 0.7 ? 'receipt_' + (index + 1) + '.pdf' : null
    }));
  };

  const generateMockSummary = () => {
    const employees = ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis', 'David Wilson'];
    const summary = {};
    
    employees.forEach(employee => {
      summary[employee] = (Math.random() * 2000 + 500).toFixed(2);
    });
    
    return summary;
  };

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      
      if (useMockData) {
        const mockData = generateMockData();
        const mockSummary = generateMockSummary();
        const currentMonthName = new Date().toLocaleDateString('en-GB', { month: 'short' });
        
        setPettyCashData(mockData);
        setSummary(mockSummary);
        setCurrentMonth(currentMonthName);
      } else {
        try {
          // Get current month dates (matching Laravel: Carbon::now()->startOfMonth() / endOfMonth())
          const now = new Date();
          const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
          const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          
          const [pettyCashResponse, summaryResponse] = await Promise.all([
            pettyCashService.getPettyCash(),
            pettyCashService.getPettyCashSummary(
              dateFrom.toISOString().split('T')[0],
              dateTo.toISOString().split('T')[0]
            )
          ]);
          
          setPettyCashData(pettyCashResponse.data || []);
          // Always use summary from API response (never calculate on frontend)
          setSummary(summaryResponse.data || {});
          setCurrentMonth(new Date().toLocaleDateString('en-GB', { month: 'short' }));
          
          // Log source for debugging
          console.log('📊 Data Source:', {
            transactions: pettyCashResponse.source || 'unknown',
            summary: summaryResponse.source || 'unknown',
            transactionCount: (pettyCashResponse.data || []).length,
            summaryEmployees: Object.keys(summaryResponse.data || {}).length
          });
          
          // Only log if using mock data (for debugging)
          if (pettyCashResponse.source === 'mock' || summaryResponse.source === 'mock') {
            console.log('📋 Using mock data - no records in database');
          } else if (pettyCashResponse.source === 'database' && summaryResponse.source === 'database') {
            console.log('✅ Connected to live database');
          }
        } catch (apiError) {
          console.error('API Error:', apiError);
          // Only use mock data if useMockData is explicitly enabled
          // Otherwise, show empty state
          if (useMockData) {
            const mockData = generateMockData();
            const mockSummary = generateMockSummary();
            const currentMonthName = new Date().toLocaleDateString('en-GB', { month: 'short' });
            
            setPettyCashData(mockData);
            setSummary(mockSummary);
            setCurrentMonth(currentMonthName);
            
            console.log('📋 Using mock data (explicitly enabled)');
          } else {
            // API failed but mock data not enabled - show empty state
            setPettyCashData([]);
            setSummary({});
            setCurrentMonth(new Date().toLocaleDateString('en-GB', { month: 'short' }));
            
            console.log('⚠️ API connection failed, showing empty state');
          }
        }
      }
    } catch (error) {
      console.error('Error loading petty cash data:', error);
      toast.error('Failed to load petty cash data');
    } finally {
      setLoading(false);
    }
  };

  // Track if component has mounted to avoid double loading
  const [hasMounted, setHasMounted] = useState(false);

  // Load data on component mount and when returning from create/edit
  useEffect(() => {
    loadData();
    setHasMounted(true);
  }, [useMockData]);

  // Reload data when navigating back from create/edit screens
  useEffect(() => {
    // Only reload if component has already mounted (to avoid double loading on initial mount)
    if (hasMounted && location.pathname === '/petty-cash') {
      // Check if we're returning from create/edit (location state indicates refresh needed)
      if (location.state?.refresh) {
        loadData();
        // Clear the refresh flag
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.pathname, location.state, hasMounted]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filtered and sorted data
  const filteredData = useMemo(() => {
    let filtered = [...pettyCashData]; // Create a copy to avoid mutating the original array
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.employee.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.date.includes(searchTerm) ||
        item.amount.toString().includes(searchTerm)
      );
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "employee") {
        aValue = a.employee || "";
        bValue = b.employee || "";
      } else if (sortField === "date") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "description") {
        aValue = a.description || "";
        bValue = b.description || "";
      } else if (sortField === "amount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [pettyCashData, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesChange = (e) => {
    setEntriesPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleColumnVisibilityToggle = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleDownload = async (id, field) => {
    try {
      // Find the specific row data
      const item = pettyCashData.find(item => item.id === id);
      if (!item) {
        toast.error('Item not found');
        return;
      }

      if (useMockData) {
        // Create CSV content for the specific row
        const csvContent = [
          ['Employee Name', 'Date', 'Description', 'Amount'],
          [item.employee, item.date, item.description, `£${item.amount}`]
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `petty-cash-${id}-${item.employee.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Row data downloaded as CSV successfully');
      } else {
        // For real data, use backend file download (backend redirects to presigned URL)
        try {
          await pettyCashService.downloadFile(id, field);
          toast.success('File download started');
        } catch (fileError) {
          console.error('File download error:', fileError);
          // If file download fails, fall back to CSV download
          const csvContent = [
            ['Employee Name', 'Date', 'Description', 'Amount'],
            [item.employee, item.date, item.description, `£${item.amount}`]
          ].map(row => row.join(',')).join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `petty-cash-${id}-${item.employee.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success('Row data downloaded as CSV successfully');
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleEdit = (id) => {
    navigate(`/petty-cash/${id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this petty cash entry and all related records? This action cannot be undone.')) {
      try {
        if (useMockData) {
          // Find the item to delete first
          const itemToDelete = pettyCashData.find(item => item.id === id);
          if (!itemToDelete) {
            toast.error('Item not found');
            return;
          }

          // Remove the specific entry
          const updatedData = pettyCashData.filter(item => item.id !== id);
          setPettyCashData(updatedData);
          
          // Always reload summary from API to ensure consistency (never recalculate on frontend)
          try {
            const now = new Date();
            const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
            const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            
            const summaryResponse = await pettyCashService.getPettyCashSummary(
              dateFrom.toISOString().split('T')[0],
              dateTo.toISOString().split('T')[0]
            );
            // Always use summary from API response
            setSummary(summaryResponse.data || {});
            console.log('✅ Summary reloaded from API after delete');
          } catch (error) {
            console.error('Error reloading summary:', error);
            // Keep existing summary if reload fails
          }
          
          toast.success('Petty cash entry and related records deleted successfully');
        } else {
          // For real data, call the service to delete
          await pettyCashService.deletePettyCash(id);
          loadData(); // Reload data to refresh both petty cash data and summary
          toast.success('Petty cash entry deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting petty cash entry:', error);
        toast.error('Failed to delete petty cash entry');
      }
    }
  };

  const handleExportCSV = () => {
    try {
      // Create headers based on visible columns
      const headers = [];
      if (visibleColumns.employee) headers.push('Employee Name');
      if (visibleColumns.date) headers.push('Date');
      if (visibleColumns.description) headers.push('Description');
      if (visibleColumns.amount) headers.push('Amount');
      
      // Create CSV content with dynamic headers
      const csvContent = [
        headers,
        ...filteredData.map(item => {
          const row = [];
          if (visibleColumns.employee) row.push(item.employee);
          if (visibleColumns.date) row.push(item.date);
          if (visibleColumns.description) row.push(item.description);
          if (visibleColumns.amount) row.push(`£${item.amount}`);
          return row;
        })
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `petty-cash-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Create PDF content using jsPDF (copied from ExpenseAccount.jsx)
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Petty Cash Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${filteredData.length}`, 20, 40);
      
      // Calculate total amount
      const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      doc.text(`Total Amount: £${totalAmount.toFixed(2)}`, 20, 50);
      
      // Add summary section
      doc.setFontSize(12);
      doc.text('Summary by Employee:', 20, 65);
      doc.setFontSize(10);
      
      let summaryY = 75;
      Object.entries(summary).forEach(([employee, amount]) => {
        doc.text(`${employee}: £${amount}`, 25, summaryY);
        summaryY += 5;
      });
      
      // Add table headers
      const headers = [];
      if (visibleColumns.employee) headers.push('Employee');
      if (visibleColumns.date) headers.push('Date');
      if (visibleColumns.description) headers.push('Description');
      if (visibleColumns.amount) headers.push('Amount');
      
      let yPosition = summaryY + 15;
      const startX = 20;
      const colWidth = 45;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      filteredData.forEach((item, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (visibleColumns.employee) rowData.push(item.employee ? item.employee.substring(0, 20) : '');
        if (visibleColumns.date) rowData.push(item.date);
        if (visibleColumns.description) rowData.push(item.description ? item.description.substring(0, 20) : '');
        if (visibleColumns.amount) rowData.push(`£${item.amount}`);
        
        rowData.forEach((cell, cellIndex) => {
          doc.text(cell.substring(0, 20), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`petty-cash-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintTable = () => {
    const printWindow = window.open('', '_blank');
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Petty Cash Table</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 12px;
          }
          h1 { 
            color: #333; 
            text-align: center; 
            margin-bottom: 20px; 
            font-size: 18px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px; 
            font-size: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px; 
            text-align: left; 
          }
          th { 
            background-color: #f8f9fa; 
            font-weight: bold; 
            font-size: 11px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Petty Cash Table</h1>
        <table>
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.map(item => `
              <tr>
                <td>${item.employee}</td>
                <td>${item.date}</td>
                <td>${item.description}</td>
                <td>£${item.amount}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="no-print">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    
    toast.success('Print window opened');
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        Loading petty cash data...
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span className={styles.breadcrumbLink} onClick={() => navigate('/dashboard')}>
          Dashboard
        </span>
        {/* <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbLink} onClick={() => navigate('/finance')}>
          Finance
        </span> */}
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>Petty Cash</span>
      </div>

      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      {/* Summary Section */}
      <div className={styles.summarySection}>
        <table className={styles.summaryTable}>
          <tbody>
            <tr className={styles.info}>
              <td colSpan="2">
                <strong>Petty Cash Summary ({currentMonth})</strong>
              </td>
            </tr>
            <tr className={styles.warning}>
              <td><strong>Employee Name</strong></td>
              <td><strong>Petty Cash Amount</strong></td>
            </tr>
            {Object.entries(summary).map(([employee, amount]) => (
              <tr key={employee}>
                <td>{employee}</td>
                <td>£{amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Main Panel */}
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h3 className={styles.panelTitle}>Petty Cash</h3>
          <button 
            className={styles.addButton}
            onClick={() => navigate('/petty-cash/create')}
          >
            <i className="glyphicon glyphicon-plus"></i> Add Expenses
          </button>
        </div>
        
        <div className={styles.panelBody}>
          {/* DataTables Controls */}
          <div className={styles.dataTableControls}>
            <div className={styles.controlsLeft}>
              <div className={styles.entriesControl}>
                <span>Show</span>
                <select
                  className={styles.entriesSelect}
                  value={entriesPerPage}
                  onChange={handleEntriesChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
              
              <div className={styles.searchControl}>
                <span>Search:</span>
                <input
                  type="text"
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder="Search..."
                />
              </div>
            </div>
            
            <div className={styles.controlsRight}>
              <div className={styles.exportButtons}>
                <button className={styles.exportButton} onClick={handleExportCSV}>
                  Export as CSV
                </button>
                <button className={styles.exportButton} onClick={handleExportPDF}>
                  Export as PDF
                </button>
                <button className={styles.exportButton} onClick={handlePrintTable}>
                  Print table
                </button>
              </div>
              
              <div className={styles.columnVisibilityContainer}>
                <button
                  className={styles.columnVisibilityButton}
                  onClick={() => setShowColumnVisibility(!showColumnVisibility)}
                >
                  Column visibility
                </button>
                {showColumnVisibility && (
                  <div className={styles.columnVisibilityDropdown}>
                    {Object.entries(visibleColumns).map(([column, visible]) => (
                      <div
                        key={column}
                        className={styles.columnVisibilityItem}
                        onClick={() => handleColumnVisibilityToggle(column)}
                      >
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => {}}
                        />
                        {column.charAt(0).toUpperCase() + column.slice(1)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {visibleColumns.employee && (
                    <th 
                      className={`sortable ${styles.employeeColumn}`}
                      onClick={() => handleSort("employee")}
                      style={{ cursor: "pointer" }}
                    >
                      Employee Name<SortIcon direction={sortField === "employee" ? sortDirection : null} isActive={sortField === "employee"} />
                    </th>
                  )}
                  {visibleColumns.date && (
                    <th 
                      className={`sortable ${styles.dateColumn}`}
                      onClick={() => handleSort("date")}
                      style={{ cursor: "pointer" }}
                    >
                      Date<SortIcon direction={sortField === "date" ? sortDirection : null} isActive={sortField === "date"} />
                    </th>
                  )}
                  {visibleColumns.description && (
                    <th 
                      className={`sortable ${styles.descriptionColumn}`}
                      onClick={() => handleSort("description")}
                      style={{ cursor: "pointer" }}
                    >
                      Description<SortIcon direction={sortField === "description" ? sortDirection : null} isActive={sortField === "description"} />
                    </th>
                  )}
                  {visibleColumns.amount && (
                    <th 
                      className={`sortable ${styles.amountColumn}`}
                      onClick={() => handleSort("amount")}
                      style={{ cursor: "pointer" }}
                    >
                      Amount<SortIcon direction={sortField === "amount" ? sortDirection : null} isActive={sortField === "amount"} />
                    </th>
                  )}
                  {visibleColumns.actions && <th className={styles.actionsColumn}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map(item => (
                    <tr key={item.id}>
                      {visibleColumns.employee && <td>{item.employee}</td>}
                      {visibleColumns.date && <td>{item.date}</td>}
                      {visibleColumns.description && <td>{item.description}</td>}
                      {visibleColumns.amount && <td className={styles.amountColumn}>£{item.amount}</td>}
                      {visibleColumns.actions && (
                        <td className={styles.actionsColumn}>
                          <div className={styles.actionButtons}>
                            {item.file && (
                              <button
                                className={`${styles.actionButton} ${styles.downloadButton}`}
                                onClick={() => handleDownload(item.id, 'file')}
                                title="Download"
                              >
                                <i className="fa fa-download"></i> Download
                              </button>
                            )}
                            <button
                              className={`${styles.actionButton} ${styles.editButton}`}
                              onClick={() => handleEdit(item.id)}
                              title="Edit"
                            >
                              <i className="fa fa-edit"></i> Edit
                            </button>
                            <button
                              className={`${styles.actionButton} ${styles.deleteButton}`}
                              onClick={() => handleDelete(item.id)}
                              title="Delete"
                            >
                              <i className="fa fa-remove"></i> Remove
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ textAlign: 'center', padding: '20px' }}>
                      No petty cash entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
              </div>
              
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (page > totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PettyCash; 